<template>
  <ModalSmall :is-visible-button-close="false" @close="closeModalRefill" class="reset-md">
    <div class="container-paymentautomatic">
      <div class="wrapper-payment">
        <div class="tit-h5">{{ t('auto_renew_prompt') }}</div>
        <label class="checkbox-wrap">
          <input :class="{ 'input-error': isErrorBase }" type="checkbox" v-model="rechargeAllow" />
          <div class="checkbox-ui">
            <IconCheck></IconCheck>
          </div>
          <p>{{ t('auto_renew_description') }}</p>
        </label>
        <div v-if="!rechargeAllow" class="error-message">
          <img src="/public/icons/caution.svg" alt="" />
          <p class="small error">
            {{ t('ss_warning_no_refill') }}
          </p>
        </div>
      </div>

      <div class="wrapper-payment">
        <p :class="['gd-txt', { dimmer: !rechargeAllow }]">
          {{ t('auto_renew_trigger_amount') }}
        </p>
        <div class="input-with-unit" style="--unit: 'Credit'" :class="{ disabled: !rechargeAllow }">
          <InputNumberWithComma
            :has-error="isErrorBase"
            :valueMin="0"
            :is-disabled="!rechargeAllow"
            :is-allowed-decimal="false"
            v-model.number="formAutoRenew.rechargeBaseAmount"
          />
        </div>
        <div v-if="isErrorBase" class="error-message">
          <IconInfo class="icon-smaller icon-error" />
          <p class="small error">
            {{ textErrorBase }}
          </p>
        </div>
      </div>

      <div class="wrapper-payment">
        <p :class="['gd-txt', { dimmer: !rechargeAllow }]">
          {{ t('auto_renew_recharge_amount') }}
        </p>

        <div class="input-with-unit" style="--unit: 'Credit'" :class="{ disabled: !rechargeAllow }">
          <InputNumberWithComma
            :has-error="isErrorUnit"
            :valueMin="0"
            :is-disabled="!rechargeAllow"
            :is-allowed-decimal="false"
            v-model.number="formAutoRenew.rechargeUnitAmount"
          />
        </div>
        <p v-if="textCostMaxEstimate">{{ t('ss_refill_unit_payment_max') }} {{ textCostMaxEstimate }}</p>
        <div v-if="isErrorUnit" class="error-message">
          <IconInfo class="icon-smaller icon-error" />
          <p class="small error">{{ textErrorUnit }}</p>
        </div>
      </div>

      <div class="wrapper-payment">
        <p :class="['gd-txt', { dimmer: !rechargeAllow }]">
          {{ t('auto_renew_monthly_limit') }}
        </p>
        <div class="input-with-unit" style="--unit: 'Credit'" :class="{ disabled: !rechargeAllow }">
          <InputNumberWithComma
            :has-error="isErrorLimit"
            :valueMin="0"
            :is-disabled="!rechargeAllow"
            :is-allowed-decimal="false"
            v-model.number="formAutoRenew.rechargeLimitAmount"
          />
        </div>
        <div v-if="isErrorLimit" class="error-message">
          <IconInfo class="icon-smaller icon-error" />
          <p class="small error">{{ textErrorLimit }}</p>
        </div>
      </div>
    </div>

    <div class="footer">
      <ButtonProcess secondary v-if="!isRequiredRefill" @click="closeModalRefill">
        {{ t('cancel') }}
      </ButtonProcess>
      <ButtonProcess :disabled="isDisabledButtonSave" @click="onClickSave">
        {{ textButtonSave }}
      </ButtonProcess>
    </div>
  </ModalSmall>
</template>

<script setup lang="ts">
import { subscriptionChangeRenew } from '@/api/apiService';
import IconCheck from '@/components/icons/IconCheck.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import InputNumberWithComma from '@/components/shared/base/InputNumberWithComma.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { useModalRefill } from '@/composables/domains/self-service/useModalRefill';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';
import { ErrorInput } from '@/types/components';
import { formatNumberWithComma } from '@/utils/stringUtils.js';

const { t } = useI18n();

const toastStore = useToastStore();
const subscriptionStore = useSubscriptionStore();

const { isRequiredRefill, closeModalRefill } = useModalRefill();

const rechargeAllow = ref(subscriptionStore.currentPlanDetails?.rechargeAllow ?? false);
const formAutoRenew = ref({
  rechargeBaseAmount: subscriptionStore.currentPlanDetails?.rechargeBaseAmount ?? null,
  rechargeUnitAmount: subscriptionStore.currentPlanDetails?.rechargeUnitAmount ?? null,
  rechargeLimitAmount: subscriptionStore.currentPlanDetails?.rechargeLimitAmount ?? null,
});

const listErrorInput = ref<ErrorInput[]>([]);

const isErrorBase = computed(() => {
  if (listErrorInput.value.length === 0) return false;
  return listErrorInput.value.some((itemError) => itemError.inputItem === 'rechargeBaseAmount');
});

const isErrorUnit = computed(() => {
  if (listErrorInput.value.length === 0) return false;
  return listErrorInput.value.some((itemError) => itemError.inputItem === 'rechargeUnitAmount');
});

const isErrorLimit = computed(() => {
  if (listErrorInput.value.length === 0) return false;
  return listErrorInput.value.some((itemError) => itemError.inputItem === 'rechargeLimitAmount');
});

const isDisabledButtonSave = computed(() => {
  const { rechargeBaseAmount, rechargeUnitAmount, rechargeLimitAmount } = formAutoRenew.value;
  if (isRequiredRefill.value) return false;
  return !rechargeAllow.value || !rechargeBaseAmount || !rechargeUnitAmount || !rechargeLimitAmount;
});

const textErrorBase = computed(() => {
  return t('ss_error_recharge_base', {
    minimum: '10,000',
    maximum: '70,000',
  });
});

const textErrorUnit = computed(() => {
  return t('ss_error_recharge_unit', {
    minimum: '10,000',
    maximum: '70,000',
  });
});

const textErrorLimit = computed(() => {
  const minimumFormatted = formatNumberWithComma(formAutoRenew.value.rechargeUnitAmount) ?? '10,000';
  return t('ss_error_recharge_limit', {
    minimum: minimumFormatted,
  });
});

const textCostMaxEstimate = computed(() => {
  if (!formAutoRenew.value.rechargeUnitAmount) return null;
  const unitAmount = formAutoRenew.value.rechargeUnitAmount;
  const isKrw = subscriptionStore.currency === 'KRW';
  const price = isKrw ? Math.round(unitAmount * 1.4) : (unitAmount / 1000).toFixed(2);
  return isKrw ? `${formatNumberWithComma(price)} ${t('label_currency_won')}` : `$${formatNumberWithComma(price)}`;
});

const textButtonSave = computed(() => {
  return rechargeAllow.value ? t('label_change') : t('button_save2');
});

// 사용자 입력 값 검증
const validateForm = () => {
  let isValid = true;
  listErrorInput.value = [];
  Object.entries(formAutoRenew.value).forEach(([key, value]) => {
    if (!value || value < 1) {
      isValid = false;
      formAutoRenew.value[key] = 0;
      listErrorInput.value.push({
        inputItem: key,
        message: '값이 0 이하일 수 없습니다.',
      });
    }
  });
  return isValid;
};

const onClickSave = async () => {
  const isValid = validateForm();
  if (!isValid) return;

  const { paymentType, plan, id } = subscriptionStore.currentPlanDetails!;
  const params = {
    ...formAutoRenew.value,
    rechargeAllow: rechargeAllow.value,
    paymentType,
    plan,
  };

  const resRecharge = await subscriptionChangeRenew(id, params);
  if (!resRecharge.success) {
    let errorInput: ErrorInput;

    switch (resRecharge.errorCode) {
      case 8003:
        errorInput = {
          inputItem: 'rechargeBaseAmount',
          message: t('ss_error_recharge_base'),
        };
        break;
      case 8004:
        errorInput = {
          inputItem: 'rechargeUnitAmount',
          message: t('ss_error_recharge_unit'),
        };
        break;
      case 8005:
        errorInput = {
          inputItem: 'rechargeLimitAmount',
          message: t('ss_error_recharge_limit'),
        };
        break;
      default:
        return;
    }
    listErrorInput.value = [errorInput];
    return;
  }

  toastStore.info(
    subscriptionStore.currentPlanDetails?.rechargeAllow
      ? t('auto_refill_success_update') // 수정 성공 안내
      : t('auto_refill_success') // 활성화 성공 안내
  );

  await subscriptionStore.fetchSubscriptionInfo();
  closeModalRefill();
};
</script>

<style scoped lang="scss">
.reset-md {
  :deep(header) {
    display: none;
  }

  .footer {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    background: #fff;
    width: 100%;
    padding-top: 40px;
  }

  @include respond-to(mobile-portrait) {
    :deep(.modal-content) {
      height: 100dvh;
      border-radius: 0;
      main {
        justify-content: space-between;
        height: 100%;
        .container-paymentautomatic {
          overflow-y: auto;
          &::-webkit-scrollbar {
            display: none;
          }
        }
      }
    }
  }
}
.container-paymentautomatic {
  .error-message {
    width: 100%;
    display: flex;
    justify-content: left;
    margin-top: 0.5rem;
    padding: $size-m;
    word-break: keep-all;
    gap: 12px;
    border-radius: 8px;
    background-color: $error-bg;
    .icon-error {
      position: relative;
      top: 0.125rem;
    }
  }
  .tit-h5 {
    line-height: 140%;
    margin-bottom: 1.5rem;
  }
}

.wrapper-payment {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.5rem;
  p {
    font-size: $font-size-psmall;
    word-break: break-all;
  }
  .gd-txt {
    font-size: $font-size-psmall;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: $new-warning;
      top: 2px;
    }
  }
}

.input-with-unit {
  position: relative;
  display: inline-block;
  width: 100%;
}

.input-with-unit > input {
  width: 100%;
  padding-right: 4.5rem;
}

.input-with-unit::after {
  content: var(--unit);
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 0.9em;
  pointer-events: none;
}

.input-with-unit.disabled::after {
  opacity: 0.5;
}

input[type='checkbox'] {
  display: none;
}

.checkbox-wrap {
  p {
    font-size: $font-size-psmall;
  }
  .checkbox-ui {
    position: relative;
    width: 1.125rem;
    height: 1.125rem;
    background-color: $color-gray-50;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    border-radius: 0.25rem;
    svg {
      color: #fff;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  input:checked + .checkbox-ui {
    background-color: var(--surface-primary);
    border-color: var(--surface-primary);
  }

  input:disabled + .checkbox-ui {
    background-color: var(--surface-gray);
    border-color: var(--border-default);
  }
}
</style>
